import { useFormik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CartContext } from "../context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { httpReauest } from "utils/httpRequest";
import { useTranslation } from "react-i18next";
import { CDN_BASE_URL } from "config";

const CheckoutForm = ({ packing, total }) => {
  const [checked, setchecked] = useState(false);
  const [countries, setCountries] = useState([]); // لیست کشورها (شامل نام و ISO2)
  const [provinces, setProvinces] = useState([]); // لیست استان‌ها/ایالت‌ها (شامل نام)
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { cart, setCart } = useContext(CartContext);

  // تابع برای دریافت لیست کشورها از countriesnow.space
  const fetchCountries = async () => {
    try {
      const response = await fetch("https://countriesnow.space/api/v0.1/countries/positions");
      const data = await response.json();
      if (data.error) {
        throw new Error(data.msg || "Error fetching countries from API.");
      }
      const sortedCountries = data.data
        .map((country) => ({
          name: country.name,
          iso2: country.iso2, // کد ISO2 کشور
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sortedCountries);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error(t("checkout.fetchCountriesError"));
    }
  };

  // تابع برای دریافت لیست استان‌ها/ایالت‌ها بر اساس نام کشور از countriesnow.space
  const fetchProvinces = async (countryName) => {
    if (!countryName) {
      setProvinces([]);
      return;
    }
    try {
      const response = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ country: countryName }),
      });
      const data = await response.json();
      if (data.error) {
        // برخی کشورها ممکن است استان نداشته باشند یا API داده‌ای برنگرداند
        if (data.msg && data.msg.includes("does not have any states")) {
          setProvinces([]); // اگر استانی ندارد، لیست را خالی کن
        } else {
          throw new Error(data.msg || "Error fetching states from API.");
        }
      } else {
        const sortedProvinces = data.data.states
          .map((state) => ({
            name: state.name, // نام استان
            // این API کد ISO2 برای استان‌ها را به صورت استاندارد برنمی‌گرداند،
            // بنابراین از نام استان به عنوان مقدار استفاده می‌کنیم.
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setProvinces(sortedProvinces);
      }
    } catch (error) {
      console.error("Error fetching provinces:", error);
      toast.error(t("checkout.fetchStatesError"));
      setProvinces([]);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
      country: "", // کد ISO2 کشور برای نمایش در select
      countryName: "", // نام کشور برای ارسال به API استان‌ها و بک‌اند
      state: "", // نام استان برای نمایش در select و ارسال به بک‌اند
      city: "",
      postalCode: "",
      allPrice: total,
      products: [],
      userId: "",
      userEmail: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = t("validation.required");
      if (!values.phone) errors.phone = t("validation.required");
      if (!values.email) {
        errors.email = t("validation.required");
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
        errors.email = t("validation.invalidEmail");
      }
      if (!values.address) errors.address = t("validation.required");
      if (!values.country) errors.country = t("validation.required"); // ISO2 کشور
      if (!values.state) errors.state = t("validation.required"); // نام استان
      return errors;
    },
    onSubmit: async (values) => {
      if (!checked) {
        toast.error(t("checkout.acceptTermsError"));
        return;
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const currentUserId = user?._id || null;
      const currentUserEmail = user?.email || values.email;

      if (!currentUserEmail) {
        toast.error(t("validation.emailRequired"));
        return;
      }

      const productsForOrder = cart?.map((e) => ({
        productId: e.id,
        count: e.count,
        price: e?.special_price || e?.price,
      }));

      const orderData = {
        ...values,
        allPrice: total,
        products: productsForOrder,
        userId: currentUserId,
        userEmail: currentUserEmail,
        country: values.country, // ارسال کد ISO2 کشور به بک‌اند (برای PayTabs)
        countryName: values.countryName, // ارسال نام کشور به بک‌اند
        state: values.state, // ارسال نام استان به بک‌اند
      };

      try {
        const headers = { "Content-Type": "application/json" };
        if (user?.token) {
          headers["x-access-token"] = user.token;
        }

        const response = await fetch(`${CDN_BASE_URL}api/order/initiate-paytabs-payment`, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(orderData),
        });

        const result = await response.json();

        if (response.ok && result.data && result.data.redirect_url) {
          window.location.href = result.data.redirect_url;
        } else {
          toast.error(result.message || t("checkout.paymentInitiationError"));
          console.error("Backend payment initiation error:", result);
        }
      } catch (err) {
        console.error("Payment initiation fetch error:", err);
        toast.error(t("checkout.paymentConnectionError"));
      }
    },
  });

  // تابع برای دریافت اطلاعات کاربر در صورت ورود (برای پر کردن خودکار فرم)
  const getData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user._id && user.token) {
      try {
        const { data } = await httpReauest(
          "GET",
          "/user/info/" + user._id,
          {},
          { "x-access-token": user.token }
        );
        Object.keys(data.data.user).forEach((item) => {
          if (formik.values.hasOwnProperty(item)) {
            formik.setFieldValue(item, data.data.user[item]);
          }
        });
        if (data.data.user.email) {
          formik.setFieldValue("email", data.data.user.email);
        }
        // اگر کاربر کشور و استان را در پروفایل خود دارد، آن‌ها را پر کنید
        if (data.data.user.country) { // user.country باید ISO2 باشد
          formik.setFieldValue("country", data.data.user.country);
          // پیدا کردن نام کشور بر اساس ISO2 برای استفاده در fetchProvinces
          const countryObj = countries.find(c => c.iso2 === data.data.user.country);
          if (countryObj) {
            formik.setFieldValue("countryName", countryObj.name);
            // fetchProvinces در useEffect پایین‌تر فراخوانی می‌شود
            if (data.data.user.state) { // user.state باید نام استان باشد
              formik.setFieldValue("state", data.data.user.state);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error(t("checkout.fetchUserInfoError"));
      }
    }
  };

  useEffect(() => {
    fetchCountries(); // دریافت کشورها هنگام mount شدن کامپوننت
    // getData در یک useEffect جداگانه فراخوانی می‌شود تا countries لود شده باشد
  }, []);

  useEffect(() => {
    // getData را فقط زمانی فراخوانی کن که لیست کشورها لود شده باشد
    if (countries.length > 0) {
      getData();
    }
  }, [countries]); // وابسته به لود شدن لیست کشورها

  // Effect برای به‌روزرسانی لیست استان‌ها هنگام تغییر کشور انتخاب شده
  useEffect(() => {
    // پیدا کردن نام کشور بر اساس ISO2 برای فراخوانی API استان‌ها
    const selectedCountry = countries.find(c => c.iso2 === formik.values.country);
    if (selectedCountry) {
      formik.setFieldValue("countryName", selectedCountry.name); // نام کشور را برای onSubmit ذخیره کن
      fetchProvinces(selectedCountry.name); // فراخوانی API با نام کشور
    } else {
      setProvinces([]);
      formik.setFieldValue("countryName", "");
      formik.setFieldValue("state", "");
    }
  }, [formik.values.country, countries]); // وابسته به تغییرات کشور و لود شدن لیست کشورها

  return (
    <>
      <form
        onSubmit={formik.handleSubmit}
        className="mt-10 bg-gray-50 px-4 pt-8 lg:mt-0 rounded-lg shadow-lg p-6 "
      >
        <p className="text-2xl font-bold text-gray-800 mb-4">{t("checkout.purchaseInfoTitle")}</p>
        <p className="text-gray-600 mb-6">{t("checkout.completeOrderInfo")}</p>

        <div>
          <div className="grid md:grid-cols-2 gap-4">
            <span>
              <label htmlFor="name" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.fullNameLabel")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.name && formik.errors.name ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.name}</div>
                ) : null}
              </div>
            </span>
            <span>
              <label htmlFor="phone" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.phoneNumberLabel")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.phone}</div>
                ) : null}
              </div>
            </span>

            <span className="">
              <label htmlFor="email" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.emailLabel")}
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                ) : null}
              </div>
            </span>

            {/* فیلد انتخاب کشور */}
            <span>
              <label htmlFor="country" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.countryLabel")}
              </label>
              <div className="relative">
                <select
                  id="country"
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                >
                  <option value="">{t("checkout.selectCountryPlaceholder")}</option>
                  {countries.map((country) => (
                    <option key={country.iso2} value={country.iso2}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {formik.touched.country && formik.errors.country ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.country}</div>
                ) : null}
              </div>
            </span>

            {/* فیلد انتخاب استان/ایالت */}
            <span>
              <label htmlFor="state" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.stateLabel")}
              </label>
              <div className="relative">
                <select
                  id="state"
                  name="state"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  disabled={!formik.values.country || provinces.length === 0}
                >
                  <option value="">{t("checkout.selectStatePlaceholder")}</option>
                  {provinces.map((province) => (
                    <option key={province.name} value={province.name}>
                      {province.name}
                    </option>
                  ))}
                </select>
                {formik.touched.state && formik.errors.state ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.state}</div>
                ) : null}
              </div>
            </span>

            {/* فیلد شهر (متنی) */}
            <span>
              <label htmlFor="city" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.cityLabel")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.city && formik.errors.city ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
                ) : null}
              </div>
            </span>

            <span>
              <label htmlFor="postalCode" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.postalCodeLabel")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.postalCode && formik.errors.postalCode ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.postalCode}</div>
                ) : null}
              </div>
            </span>

            <span className="col-span-full">
              <label htmlFor="address" className="mt-4 mb-2 block text-sm font-medium text-gray-700">
                {t("checkout.addressLabel")}
              </label>
              <div className="relative">
                <textarea
                  id="address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows="3"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                />
                {formik.touched.address && formik.errors.address ? (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.address}</div>
                ) : null}
              </div>
            </span>

            <span className="col-span-full">
              <p className="text-gray-600 text-sm mt-4">{t("checkout.privacyPolicyText")}</p>
              <span className="flex gap-2 mt-4 items-center">
                <input
                  onClick={() => setchecked(!checked)}
                  checked={checked}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="terms" className="text-gray-700 text-sm cursor-pointer">
                  {t("checkout.termsAndConditionsAccept")}
                </label>
              </span>
            </span>
          </div>
        </div>
        <button
          type="submit"
          className="mt-8 w-full rounded-md bg-blue-600 hover:bg-blue-700 px-6 py-3 font-medium text-white shadow-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!checked || formik.isSubmitting}
        >
          {formik.isSubmitting ? t("checkout.processingButton") : t("checkout.createInvoiceButton")}
        </button>
      </form>
    </>
  );
};

export default CheckoutForm;
